import { createSlice } from "@reduxjs/toolkit";
import {
    getAutomatedWorkflowStepSchemasAPI,
    getAutomatedWorkflowAPI,
    updateAutomatedWorkflowAPI,
    updateAutomatedWorkflowStepTreeAPI,
    validateAutomatedWorkflowAPI,
    deleteAutomatedWorkflowAPI,
    getAutomatedWorkflowExecutionsAPI,
    getAutomatedWorkflowExecutionAPI,
} from "./apis/automated-workflow";
import Polling from "@cores/polling";

const initialState = {
    stepSchemas: { pending: false, error: false, data: null },
    detail: { pending: false, error: false, data: null },
    executions: { pending: false, error: false, data: null },
    executionDetail: { pending: false, error: false, data: null },
    validation: { pending: false, error: false, data: null },
};

const automatedWorkflowSlice = createSlice({
    name: "automatedWorkflow",
    initialState,
    reducers: {
        resetStepSchemas(state) {
            state.stepSchemas = { pending: false, error: false, data: null };
        },
        stepSchemasPending(state) {
            state.stepSchemas = { pending: true, error: false, data: null };
        },
        stepSchemasSuccess(state, action) {
            state.stepSchemas = { pending: false, error: false, data: action.payload };
        },
        stepSchemasFailure(state) {
            state.stepSchemas = { pending: false, error: true, data: null };
        },
        resetDetail(state) {
            state.detail = { pending: false, error: false, data: null };
        },
        detailPending(state) {
            // data 는 유지(재조회 중 깜빡임 방지). 초기 로드는 어차피 null, 워크플로우 전환 시 resetDetail 이 선처리.
            state.detail = { ...state.detail, pending: true, error: false };
        },
        detailSuccess(state, action) {
            state.detail = { pending: false, error: false, data: action.payload };
        },
        detailFailure(state) {
            state.detail = { pending: false, error: true, data: null };
        },
        resetExecutions(state) {
            state.executions = { pending: false, error: false, data: null };
        },
        executionsPending(state) {
            // data 는 유지(재조회/페이지 이동 중 깜빡임 방지). 워크플로우 전환 시 resetExecutions 이 선처리.
            state.executions = { ...state.executions, pending: true, error: false };
        },
        executionsSuccess(state, action) {
            state.executions = { pending: false, error: false, data: action.payload };
        },
        executionsFailure(state) {
            state.executions = { pending: false, error: true, data: null };
        },
        resetExecutionDetail(state) {
            state.executionDetail = { pending: false, error: false, data: null };
        },
        executionDetailPending(state) {
            // data 는 유지(재조회 중 깜빡임 방지). 실행이력 전환 시 resetExecutionDetail 이 선처리.
            state.executionDetail = { ...state.executionDetail, pending: true, error: false };
        },
        executionDetailSuccess(state, action) {
            state.executionDetail = { pending: false, error: false, data: action.payload };
        },
        executionDetailFailure(state) {
            state.executionDetail = { pending: false, error: true, data: null };
        },
        resetValidation(state) {
            state.validation = { pending: false, error: false, data: null };
        },
        validationPending(state) {
            state.validation = { pending: true, error: false, data: null };
        },
        validationSuccess(state, action) {
            state.validation = { pending: false, error: false, data: action.payload };
        },
        validationFailure(state, action) {
            // 네트워크/HTTP 오류 전용. 검증 위반(status: "INVALID")은 200 으로 내려와 validationSuccess 로 처리됨.
            state.validation = { pending: false, error: true, data: action.payload ?? null };
        },
        // 특정 스텝(stepName=node.id)의 서버 검증 위반만 제거. 조건 수정 시 해당 노드의 stale 경고만 지운다.
        clearStepViolations(state, action) {
            const violations = state.validation.data?.violations;
            if (Array.isArray(violations)) {
                state.validation.data.violations = violations.filter((v) => v.stepName !== action.payload);
            }
        },
    },
});

export const automatedWorkflowActions = automatedWorkflowSlice.actions;
export const { resetStepSchemas, resetDetail, resetExecutions, resetExecutionDetail, resetValidation, clearStepViolations } = automatedWorkflowActions;

/**
 * Automated Workflow step schema 전체 목록 조회.
 * nextToken이 null이 될 때까지 반복 호출하여 전체 결과를 누적해 반환한다.
 */
export const getStepSchemas =
    (arg = {}) =>
    async (dispatch, getState) => {
        const { stage, project } = getState();
        dispatch(automatedWorkflowActions.stepSchemasPending());
        try {
            let nextToken = null;
            let results = [];
            do {
                const { data } = await getAutomatedWorkflowStepSchemasAPI(stage.id, stage.endpoint, project.id, {
                    ...arg,
                    nextToken,
                });
                results = results.concat(data?.results ?? []);
                nextToken = data?.nextToken ?? null;
            } while (nextToken);
            dispatch(automatedWorkflowActions.stepSchemasSuccess(results));
            return results;
        } catch (error) {
            dispatch(automatedWorkflowActions.stepSchemasFailure());
            throw error;
        }
    };

/**
 * Automated Workflow 단건 상세 조회.
 * - detail state(pending/error/data) 를 갱신하고 응답 데이터를 반환한다.
 * @param {string} workflowId
 */
export const getAutomatedWorkflow = (workflowId) => async (dispatch, getState) => {
    const { stage, project } = getState();
    dispatch(automatedWorkflowActions.detailPending());
    try {
        const { data } = await getAutomatedWorkflowAPI(stage.id, stage.endpoint, project.id, workflowId);
        dispatch(automatedWorkflowActions.detailSuccess(data));
        return data;
    } catch (error) {
        dispatch(automatedWorkflowActions.detailFailure());
        throw error;
    }
};

/**
 * Automated Workflow 수정(이름/설명/상태 등 부분 수정).
 * - 리듀서 상태는 변경하지 않고 API Promise 만 반환 → 호출 측에서 새로고침/알림 처리.
 * @param {string} workflowId
 * @param {object} body - { name, description, status } 중 변경 필드 (partial)
 */
export const updateAutomatedWorkflow = (workflowId, body) => (dispatch, getState) => {
    const { stage, project } = getState();
    return updateAutomatedWorkflowAPI(stage.id, stage.endpoint, project.id, workflowId, body);
};

/**
 * Automated Workflow step tree 전체 덮어쓰기(PUT).
 * - 리듀서 상태는 변경하지 않고 API Promise 만 반환 → 호출 측에서 새로고침/알림 처리.
 * - 요청 본문은 { stepTree } 형태로 래핑하여 전송.
 * @param {string} workflowId
 * @param {object} stepTree - buildStepTree 결과(START 루트 트리). 기존 step tree 를 전체 교체.
 */
export const updateAutomatedWorkflowStepTree = (workflowId, stepTree) => (dispatch, getState) => {
    const { stage, project } = getState();
    return updateAutomatedWorkflowStepTreeAPI(stage.id, stage.endpoint, project.id, workflowId, { stepTree });
};

/**
 * Automated Workflow 유효성 검증(VALIDATE 액션).
 * - validation state(pending/error/data) 를 갱신하고 응답 데이터를 반환한다.
 * - 검증 위반(status: "INVALID")도 HTTP 200 으로 내려오므로 error 가 아니라 validationSuccess(data)로 저장한다.
 *   → 통과/실패 판정은 호출 측(handleSave)에서 data.status 로 수행한다.
 * - catch 는 네트워크/HTTP 오류 전용이며, 예외를 재전파해 호출 측에서 저장을 중단할 수 있다.
 * - 응답 data 형태: { status: "COMPLETE" | "INCOMPLETE" | "INVALID", violations: [{ rule, message, stepName }] }
 * - 요청 본문은 { action: "VALIDATE", data: { stepTree } } 형태로 전송(API 레이어에서 조립).
 * @param {string} workflowId
 * @param {object} stepTree - buildStepTree 결과(START 루트 트리). 검증 대상.
 */
export const validateAutomatedWorkflow = (workflowId, stepTree) => async (dispatch, getState) => {
    const { stage, project } = getState();
    dispatch(automatedWorkflowActions.validationPending());
    try {
        const { data } = await validateAutomatedWorkflowAPI(stage.id, stage.endpoint, project.id, workflowId, stepTree);
        dispatch(automatedWorkflowActions.validationSuccess(data));
        return data;
    } catch (error) {
        // 실패 응답 본문이 있으면 함께 저장(없으면 null)
        dispatch(automatedWorkflowActions.validationFailure(error?.response?.data ?? null));
        throw error;
    }
};

/**
 * Automated Workflow 삭제.
 * - 리듀서 상태는 변경하지 않고 API Promise 만 반환 → 호출 측에서 새로고침/알림 처리.
 * @param {string} workflowId
 */
export const deleteAutomatedWorkflow = (workflowId) => (dispatch, getState) => {
    const { stage, project } = getState();
    return deleteAutomatedWorkflowAPI(stage.id, stage.endpoint, project.id, workflowId);
};

/**
 * Automated Workflow 실행이력 목록 조회.
 * - executions state(pending/error/data) 를 갱신하고 응답 데이터를 반환한다.
 * - params 로 페이지네이션(nextToken)/필터를 전달할 수 있으며, 응답을 그대로 저장하므로
 *   누적/페이지 병합이 필요하면 호출 측에서 처리한다.
 * @param {string} workflowId
 * @param {object} [params] - { nextToken, ... } 토큰 기반 페이지네이션 등
 */
export const getAutomatedWorkflowExecutions =
    (workflowId, params = {}) =>
    async (dispatch, getState) => {
        const { stage, project } = getState();
        dispatch(automatedWorkflowActions.executionsPending());
        try {
            const { data } = await getAutomatedWorkflowExecutionsAPI(
                stage.id,
                stage.endpoint,
                project.id,
                workflowId,
                params,
            );
            dispatch(automatedWorkflowActions.executionsSuccess(data));
            return data;
        } catch (error) {
            dispatch(automatedWorkflowActions.executionsFailure());
            throw error;
        }
    };

/**
 * Automated Workflow 실행이력 단건 상세 조회.
 * - executionDetail state(pending/error/data) 를 갱신하고 응답 데이터를 반환한다.
 * @param {string} workflowId
 * @param {string} executionId
 */
export const getAutomatedWorkflowExecution = (workflowId, executionId) => async (dispatch, getState) => {
    const { stage, project } = getState();
    dispatch(automatedWorkflowActions.executionDetailPending());
    try {
        const { data } = await getAutomatedWorkflowExecutionAPI(
            stage.id,
            stage.endpoint,
            project.id,
            workflowId,
            executionId,
        );
        dispatch(automatedWorkflowActions.executionDetailSuccess(data));
        return data;
    } catch (error) {
        dispatch(automatedWorkflowActions.executionDetailFailure());
        throw error;
    }
};

// 실행 종결 상태 — 이 상태에 도달하면 실행이력 상세 폴링을 중단한다 (진행 중: QUEUED/READY/RUNNING)
export const EXECUTION_TERMINAL_STATUSES = ["COMPLETED", "FAILED", "EXPIRED"];

/**
 * Automated Workflow 실행이력 단건 상세 폴링 (4초 주기, ai-workspace 폴링과 동일 패턴).
 * - 종결 상태(EXECUTION_TERMINAL_STATUSES) 도달 시 자동 중단.
 * - 상세 페이지 이탈/다른 실행 전환 시(executionDetail.data.id 불일치) 조회 없이 중단.
 *   (언마운트 cleanup 의 resetExecutionDetail 이 data 를 null 로 만들어 자동 중단된다)
 * - pending 을 건드리지 않는 사일런트 갱신이며, 폴링 중 에러는 화면(data)을 유지한 채 중단만 한다.
 * @param {string} workflowId
 * @param {string} executionId
 */
export const pollAutomatedWorkflowExecution = (workflowId, executionId) => (dispatch, getState) => {
    const { stage, project } = getState();
    Polling(
        async () => {
            if (getState().automatedWorkflow.executionDetail.data?.id !== executionId) return null;
            return await getAutomatedWorkflowExecutionAPI(
                stage.id,
                stage.endpoint,
                project.id,
                workflowId,
                executionId,
            );
        },
        (response) => !response || EXECUTION_TERMINAL_STATUSES.includes(response.data.status),
        (response) => {
            if (response?.data) dispatch(automatedWorkflowActions.executionDetailSuccess(response.data));
        },
        (error) => {
            console.error("error while polling automated workflow execution", error);
        },
        4000,
    ).run();
};

export default automatedWorkflowSlice.reducer;
